import { Grid, Icon, Stack } from '@mui/material';
import { Divider } from '@chr/eds-react';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { ShipmentDetailsItem } from '../../common/components/shipmentDetailsItem/ShipmentDetailsItem';
import { ShipmentDetailsLocation } from '../../common/components/shipmentDetailsLocation/ShipmentDetailsLocation';
import { Job } from '../../common/apis/dtos';
import { IsoToDateAtTime } from '../../common/utilities/display-utilities';

import { useShipmentDetailsData } from '../../common/hooks/useShipmentDetailsData';
import {
  getPickupValues,
  getDeliveryValues,
  isOceanShipment,
} from '../../common/utilities/shipmentDetailsUtils';
import { useChrLdFlag } from '@chr/common-launchdarkly';

export interface ShipmentDetailsInfoProps {
  shipment: Job;
  isMobileScreen: boolean;
}

export const ShipmentDetailsInfo = ({
  shipment,
  isMobileScreen,
}: ShipmentDetailsInfoProps) => {
  const translate = useTranslation();
  const { pickup, deliverTo } = useShipmentDetailsData(shipment);
  const enhancedSearch = useChrLdFlag('enhanced-externaltracking-search', false);

  const isOcean = isOceanShipment(shipment.mode);
  const pickupValues = getPickupValues(shipment, pickup, translate);
  const deliveryValues = getDeliveryValues(shipment, deliverTo, translate);

  return (
    <>
      <Grid item xs={12} lg={3}>
        <ShipmentDetailsItem
          title={enhancedSearch ? (shipment.matchedField || "Tracking Number") : "Tracking Number"}
          information={enhancedSearch ? (shipment.matchedValue || shipment.chrTrackingNumber) : shipment.chrTrackingNumber}
          isMobileScreen={isMobileScreen}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <ShipmentDetailsItem
          title="Estimated Arrival"
          information={IsoToDateAtTime(shipment.estimatedArrival, translate)}
          isMobileScreen={isMobileScreen}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <ShipmentDetailsItem
          title="Status"
          information={shipment.status}
          riskLevel={shipment.riskLevel}
          isMobileScreen={isMobileScreen}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <ShipmentDetailsItem
          title="Shipping Method"
          information={shipment.mode}
          shippingMode={shipment.mode}
          isMobileScreen={isMobileScreen}
        />
      </Grid>
      {isMobileScreen && (
        <Divider
          orientation="horizontal"
          id="horizontal-divider"
          emphasis="low"
          data-testid="mobile-horizontal-divider"
        />
      )}
      <Grid
        item
        xs={12}
        className="delivery-info-container"
        alignItems="center"
        sx={{
          paddingBottom: 2,
          background: 'white',
          paddingRight: isMobileScreen ? 1 : 0,
          paddingLeft: isMobileScreen ? '12px' : 0,
        }}
      >
        <Stack
          direction={isMobileScreen ? 'column' : 'row'}
          width={isMobileScreen ? '99%' : '100%'}
          alignItems="center"
          sx={{
            background: isMobileScreen ? '#F6F7F8' : 'inherit',
            border: isMobileScreen ? '1px solid #E5E6E7' : '',
            borderRadius: isMobileScreen ? '4px' : '0px',
            paddingTop: isMobileScreen ? 2 : 0,
            paddingBottom: isMobileScreen ? 2 : 0,
          }}
        >
          <ShipmentDetailsLocation
            title="Pickup from"
            locationLabel="Location"
            location={pickup.location}
            values={pickupValues}
            isMobileScreen={isMobileScreen}
            width={isOcean ? '30%' : '50%'}
          />
          <Icon sx={{ fontSize: 30 }} baseClassName="material-icons-outlined">
            {isMobileScreen ? 'arrow_downward' : 'arrow_right_alt'}
          </Icon>
          <ShipmentDetailsLocation
            title="Deliver to"
            locationLabel="Location"
            location={deliverTo.location}
            values={deliveryValues}
            isMobileScreen={isMobileScreen}
            width={isOcean ? '70%' : '50%'}
          />
        </Stack>
      </Grid>
    </>
  );
};
